import React, { useEffect, useRef, useState } from 'react';
import 'ol/ol.css';
import './Map.css';
import OlMap from 'ol/Map';
import OlView from 'ol/View';
import { defaults as defaultControls } from 'ol/control';
import { ZoomSlider, ScaleLine, Attribution, MousePosition } from 'ol/control';
import { format } from 'ol/coordinate';
import { Tile as OlTileLayer, Vector as OlVectorLayer } from 'ol/layer';
import { OSM as OlSourceOSM, WMTS as OlWMTSSource } from 'ol/source';
import XYZ from 'ol/source/XYZ';
import { getWidth, getTopLeft } from 'ol/extent';
import { get as getProjection } from 'ol/proj';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { sourceVector } from './MapLayers';
import { stylePolygonBasic } from './MapStyles';
import {
  Button,
  Container,
  Segment,
  Dropdown,
} from 'semantic-ui-react';
import { NavLink } from 'react-router-dom';
import _ from 'lodash';

const ACTORS_LABELS = {
  individu_individu:     'Individu ho Individu',
  individu_komunidade:   'Individu ho Komunidade',
  individu_estado:       'Indivídu ho Estado',
  komunidade_estado:     'Komunidade ho Estado',
  komunidade_kompania:   'Komunidade ho Empreza Privada',
  asosiasaun_governu:    'Asosiasaun ka Fundasaun ho Estado',
  komunidade_igreija:    'Komunidade ho Organizasaun Relijioza',
  komunidade_autoridade: 'Komunidade ho Autoridade Lokal',
  nst:                   'Nst',
  komunidade_governu:    'Komunidade ho Estado',
  komunidade_komunidade: 'Komunidade ho Komunidade',
};

// Build WMTS tile grid for IGN (computed once, stateless)
const projection = getProjection('EPSG:3857');
const projectionExtent = projection.getExtent();
const originTopLeft = getTopLeft(projectionExtent);
const size = getWidth(projectionExtent) / 256;
const resolutions = Array.from({ length: 22 }, (_, z) => size / Math.pow(2, z));
const matrixIds = Array.from({ length: 22 }, (_, z) => z.toString());
const wmtsTileGrid = new WMTSTileGrid({
  origin: originTopLeft,
  resolutions,
  matrixIds,
});

const MAPBOX_TOKEN =
  'pk.eyJ1IjoiaHVnb2xlaG91eCIsImEiOiItOHl6Sm5jIn0.12l_k0K_Z28UE-Jc0kDgpw';

const baseMapOptions = [
  { key: 1, text: 'OSM', value: 1, image: { avatar: false, src: '/assets/osm.png' } },
  { key: 2, text: 'Satellite (IGN)', value: 2, image: { avatar: false, src: '/assets/satellite.png' } },
  { key: 3, text: 'CartoDB', value: 3, image: { avatar: false, src: '/assets/cartodb.png' } },
  { key: 4, text: 'Mapbox Terrain', value: 4, image: { avatar: false, src: '/assets/cartodb.png' } },
  { key: 5, text: 'Mapbox Satellite', value: 5, image: { avatar: false, src: '/assets/cartodb.png' } },
  { key: 6, text: 'Mapbox Street', value: 6, image: { avatar: false, src: '/assets/cartodb.png' } },
];

const TheMap = () => {
  const mapDivRef = useRef(null);
  const olmapRef = useRef(null);
  const baseLayersRef = useRef({});

  const [showing, setShowing] = useState(false);
  const [panelContent, setPanelContent] = useState({});

  useEffect(() => {
    // Create fresh layer instances — never reuse module-level singletons for OlMap
    const layerOSM = new OlTileLayer({
      source: new OlSourceOSM(),
      visible: true,
    });

    const layerCartoDB = new OlTileLayer({
      source: new OlSourceOSM({
        url: 'https://{a-c}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
      }),
      visible: false,
    });

    const layerMapboxTerrain = new OlTileLayer({
      visible: false,
      source: new XYZ({
        url: `https://api.mapbox.com/v4/mapbox.outdoors/{z}/{x}/{y}.png?access_token=${MAPBOX_TOKEN}`,
      }),
    });

    const layerMapboxSatellite = new OlTileLayer({
      visible: false,
      source: new XYZ({
        url: `https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}.png?access_token=${MAPBOX_TOKEN}`,
      }),
    });

    const layerMapboxStreet = new OlTileLayer({
      visible: false,
      source: new XYZ({
        url: `https://api.mapbox.com/v4/mapbox.streets/{z}/{x}/{y}.png?access_token=${MAPBOX_TOKEN}`,
      }),
    });

    const layerIgnOrtho = new OlTileLayer({
      visible: false,
      opacity: 1.0,
      source: new OlWMTSSource({
        crossOrigin: 'anonymous',
        url: 'https://data.geopf.fr/wmts?',
        layer: 'ORTHOIMAGERY.ORTHOPHOTOS',
        matrixSet: 'PM',
        format: 'image/jpeg',
        projection,
        tileGrid: wmtsTileGrid,
        style: 'normal',
      }),
    });

    const layerCases = new OlVectorLayer({
      source: sourceVector,
      style: stylePolygonBasic,
    });

    // Store base layers by key for fast lookup in changeBasemap
    baseLayersRef.current = {
      1: layerOSM,
      2: layerIgnOrtho,
      3: layerCartoDB,
      4: layerMapboxTerrain,
      5: layerMapboxSatellite,
      6: layerMapboxStreet,
    };

    // Fresh controls — avoid singleton control reuse across mounts
    const zoomSlider = new ZoomSlider();
    const scaleLine = new ScaleLine();
    const attribution = new Attribution({ collapsible: true, collapsed: true });
    const mousePosition = new MousePosition({
      undefinedHTML: '<span class="mousePosition"> No data</span>',
      projection: 'EPSG:4326',
      coordinateFormat: (coordinate) =>
        format(coordinate, '<span class="mousePosition"> {x} ° | {y} °</span>', 4),
    });

    const map = new OlMap({
      target: mapDivRef.current,
      controls: defaultControls({ attribution: false }).extend([
        zoomSlider,
        scaleLine,
        attribution,
        mousePosition,
      ]),
      layers: [
        layerOSM,
        layerIgnOrtho,
        layerCartoDB,
        layerMapboxTerrain,
        layerMapboxSatellite,
        layerMapboxStreet,
        layerCases,
      ],
      view: new OlView({
        projection: 'EPSG:3857',
        center: [13900305.064, -1085431.521],
        zoom: 8,
        minResolution: 5,
      }),
    });

    olmapRef.current = map;

    // Load cases if not already loaded
    if (sourceVector.getFeatures().length === 0) {
      sourceVector.refresh();
    }

    const handleClick = (evt) => {
      const feature = layerCases
        .getSource()
        .getClosestFeatureToCoordinate(evt.coordinate);

      if (!feature) return;

      const extent = feature.getGeometry().getExtent();
      map.getView().fit(extent, {
        padding: [150, 150, 150, 150],
        duration: 1000,
        minResolution: 5,
      });

      setPanelContent({
        project_id:        feature.getId(),
        ref_kazu:          feature.get('ref_kazu'),
        name:              feature.get('name'),
        municipality:      feature.get('municipality'),
        postu:             feature.get('postu'),
        suku:              feature.get('suku'),
        aldeia:            feature.get('aldeia'),
        actors:            feature.get('actors'),
        conflictarea:      feature.get('conflictarea'),
        impactedhouseholds:feature.get('impactedhouseholds'),
        impactedfamilymembers: feature.get('impactedfamilymembers'),
        juridicsituationclaming: feature.get('juridicsituationclaming'),
      });
      setShowing(true);
    };

    map.on('click', handleClick);

    return () => {
      map.un('click', handleClick);
      map.setTarget(null);
      olmapRef.current = null;
    };
  }, []); // runs once on mount

  const changeBasemap = (_event, { value }) => {
    const layers = baseLayersRef.current;
    Object.keys(layers).forEach((key) => {
      layers[key].setVisible(Number(key) === value);
    });
  };

  return (
    <div
      ref={mapDivRef}
      style={{
        position: 'absolute',
        width: '100%',
        height: '100vh',
        top: '0px',
        left: '0px',
      }}
    >
      <Button.Group
        style={{
          position: 'relative',
          top: '5rem',
          left: '1rem',
          zIndex: '1',
        }}
      >
        <Dropdown
          icon='globe'
          text='Basemaps'
          options={baseMapOptions}
          floating
          labeled
          button
          className='icon'
          onChange={changeBasemap}
          style={{ whiteSpace: 'nowrap' }}
        />
      </Button.Group>

      <Container
        id='sidebar'
        style={{
          position: 'absolute',
          marginTop: '6.5rem',
          paddingLeft: '1rem',
          width: '30%',
          zIndex: '1',
          display: showing ? 'block' : 'none',
        }}
      >
        <Segment.Group>
          <Segment attached='top' style={{ paddingBottom: '0.5rem' }}>
            {panelContent.ref_kazu && (
              <div style={{ fontSize: '0.78em', color: '#888', marginBottom: '0.2rem' }}>
                {panelContent.ref_kazu}
              </div>
            )}
            <strong style={{ fontSize: '1.05em' }}>{_.capitalize(panelContent.name)}</strong>
          </Segment>
          <Segment attached='bottom'>

            {/* Localisation */}
            <div style={{ marginBottom: '0.6rem' }}>
              <span style={{ fontWeight: 'bold', fontSize: '0.8em', color: '#555', textTransform: 'uppercase' }}>
                Lokalizasaun
              </span>
              <div style={{ marginTop: '0.2rem', fontSize: '0.9em' }}>
                {[panelContent.municipality, panelContent.postu, panelContent.suku, panelContent.aldeia]
                  .filter(Boolean).join(' › ')}
              </div>
            </div>

            {/* Actors */}
            {panelContent.actors && (
              <div style={{ marginBottom: '0.6rem' }}>
                <span style={{ fontWeight: 'bold', fontSize: '0.8em', color: '#555', textTransform: 'uppercase' }}>
                  Parte sira husi Disputa
                </span>
                <ul style={{ margin: '0.2rem 0 0 1rem', padding: 0, fontSize: '0.85em' }}>
                  {panelContent.actors.split(',').map((v, i) => (
                    <li key={i}>{ACTORS_LABELS[v.trim()] || v.trim()}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Surface & population */}
            {(panelContent.conflictarea > 0 || panelContent.impactedhouseholds > 0 || panelContent.impactedfamilymembers > 0) && (
              <div style={{ marginBottom: '0.6rem', display: 'flex', gap: '1.2rem', flexWrap: 'wrap' }}>
                {panelContent.conflictarea > 0 && (
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '0.8em', color: '#555', textTransform: 'uppercase' }}>Surface</div>
                    <div style={{ fontSize: '0.9em' }}>{parseFloat(panelContent.conflictarea).toLocaleString('fr')} ha</div>
                  </div>
                )}
                {panelContent.impactedhouseholds > 0 && (
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '0.8em', color: '#555', textTransform: 'uppercase' }}>Uma Kain</div>
                    <div style={{ fontSize: '0.9em' }}>{parseInt(panelContent.impactedhouseholds).toLocaleString('fr')}</div>
                  </div>
                )}
                {panelContent.impactedfamilymembers > 0 && (
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '0.8em', color: '#555', textTransform: 'uppercase' }}>Membru Familia</div>
                    <div style={{ fontSize: '0.9em' }}>{parseInt(panelContent.impactedfamilymembers).toLocaleString('fr')}</div>
                  </div>
                )}
              </div>
            )}

            {/* Kronolojia truncated */}
            {panelContent.juridicsituationclaming && (
              <div style={{ marginBottom: '0.6rem' }}>
                <div style={{ fontWeight: 'bold', fontSize: '0.8em', color: '#555', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                  Kronolojia
                </div>
                <div style={{ fontSize: '0.85em', color: '#444', lineHeight: '1.4' }}>
                  {_.truncate(panelContent.juridicsituationclaming, { length: 300, separator: /,?\.* +/ })}
                </div>
              </div>
            )}

            {/* Buttons */}
            <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
              <Button size='mini' onClick={() => setShowing(false)}>Close</Button>
              <Button size='mini' color='teal' as={NavLink} to={`/cases/${panelContent.project_id}`}>
                See more
              </Button>
            </div>

          </Segment>
        </Segment.Group>
      </Container>
    </div>
  );
};

export default TheMap;
